import type { CalendarUpdateInput } from "@/lib/queries";
import type {
  AvailabilityRange, Calendar, CalendarDistribution, CalendarSlot, CalendarStaff, CalendarType,
  MeetingLocationKind, RescheduleOwner, StaffPriority, Weekday,
} from "@/lib/types";

/**
 * Calendars as the console shows and edits them (ADR-085 in `platform`): the words for each
 * setting, the form the edit page holds, and the checks it makes before a save.
 *
 * The rules are core-api's: `CalendarCreate`/`CalendarUpdate` in `schemas.py` and
 * `services/core-api/src/calendars.py`. The copies here keep a save the server would refuse off
 * the screen and say why first; the server decides. Pure, for `test/calendars.test.ts`.
 */

export const CALENDAR_TYPES: readonly CalendarType[] = ["personal", "round_robin", "class", "collaborative"];

/** The type picker's cards, in GoHighLevel's words (ACL-252). */
export const TYPE_INFO: Record<CalendarType, { label: string; description: string; example: string }> = {
  personal: {
    label: "Personal booking",
    description: "Schedules one-on-one meetings with a specific team member.",
    example: "Client meetings, private consultations.",
  },
  round_robin: {
    label: "Round robin",
    description: "Distributes appointments among team members in a rotating order.",
    example: "Sales calls, onboarding sessions.",
  },
  class: {
    label: "Class booking",
    description: "One host meets with multiple participants.",
    example: "Webinars, group training, online classes.",
  },
  collaborative: {
    label: "Collaborative booking",
    description: "Multiple hosts meet with one participant.",
    example: "Panel interviews, committee reviews.",
  },
};

/** The types that take one host (`SINGLE_HOST_TYPES` in core-api). */
export function singleHost(type: CalendarType): boolean {
  return type === "personal" || type === "class";
}

export const PRIORITIES: readonly StaffPriority[] = ["high", "medium", "low"];
export const PRIORITY_LABELS: Record<StaffPriority, string> = {
  high: "High priority",
  medium: "Medium priority",
  low: "Low priority",
};

export const LOCATION_KINDS: readonly MeetingLocationKind[] = ["phone", "address", "custom"];
export const LOCATION_LABELS: Record<MeetingLocationKind, string> = {
  phone: "Phone call",
  address: "Physical address",
  custom: "Custom",
};
export const LOCATION_PLACEHOLDERS: Record<MeetingLocationKind, string> = {
  phone: "Leave empty to call the contact's own number",
  address: "Street, city, state",
  custom: "How the meeting happens: a link, instructions…",
};

export const DISTRIBUTION_INFO: Record<CalendarDistribution, { label: string; description: string }> = {
  availability: {
    label: "Optimize for availability",
    description: "Offer every time at least one staff member is free, favouring higher priority.",
  },
  equal: {
    label: "Optimize for equal distribution",
    description: "Spread meetings evenly across the staff, favouring higher priority.",
  },
};

export const RESCHEDULE_INFO: Record<RescheduleOwner, { label: string; description: string }> = {
  keep: {
    label: "Keep the appointment owner",
    description: "A rescheduled meeting stays with the staff member it was booked with.",
  },
  reassign: {
    label: "Reassign by round robin",
    description: "A rescheduled meeting goes to whoever round robin picks for the new time.",
  },
};

/** Monday first, as core-api numbers them. */
export const WEEKDAYS: readonly Weekday[] = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
export const WEEKDAY_LABELS: Record<Weekday, string> = {
  mon: "Monday",
  tue: "Tuesday",
  wed: "Wednesday",
  thu: "Thursday",
  fri: "Friday",
  sat: "Saturday",
  sun: "Sunday",
};

/** The week in the order a calendar starts it. */
export function weekFrom(start: Weekday): Weekday[] {
  const i = WEEKDAYS.indexOf(start);
  return [...WEEKDAYS.slice(i), ...WEEKDAYS.slice(0, i)];
}

export const DURATION_PRESETS: readonly number[] = [15, 30, 45, 60, 90];
export const LOCALES: readonly { value: string; label: string }[] = [
  { value: "en-US", label: "English (United States)" },
  { value: "es-US", label: "Spanish (United States)" },
  { value: "en-GB", label: "English (United Kingdom)" },
];

/** The US zones first, then this browser's own if it is not one of them. */
export function timezoneOptions(own?: string): string[] {
  const us = ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles"];
  return own && !us.includes(own) ? [...us, own] : us;
}

/** "30 min", "1 hr", "1 hr 30 min", "2 days". */
export function minutesText(minutes: number): string {
  if (minutes === 0) return "None";
  if (minutes % 1440 === 0) {
    const days = minutes / 1440;
    return `${days} day${days === 1 ? "" : "s"}`;
  }
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return [h ? `${h} hr` : "", m ? `${m} min` : ""].filter(Boolean).join(" ");
}

// --- hours ---------------------------------------------------------------------------------------

const HHMM_RE = /^([01]\d|2[0-4]):([0-5]\d)$/;

/** "08:30" → 510; null when it is not a time of day. "24:00" is the end of the day. */
export function toMinutes(value: string): number | null {
  const m = HHMM_RE.exec(value);
  if (!m) return null;
  const minutes = Number(m[1]) * 60 + Number(m[2]);
  return minutes > 1440 ? null : minutes;
}

export function fromMinutes(minutes: number): string {
  const clamped = Math.max(0, Math.min(1440, minutes));
  return `${String(Math.floor(clamped / 60)).padStart(2, "0")}:${String(clamped % 60).padStart(2, "0")}`;
}

/** Every quarter hour from 00:00 to 24:00, the choices a range's start and end pick from. */
export function timeChoices(step = 15): string[] {
  const out: string[] = [];
  for (let m = 0; m <= 1440; m += step) out.push(fromMinutes(m));
  return out;
}

/** "13:30" as the calendar shows it: "1:30 PM" on a 12-hour clock. "24:00" is midnight. */
export function timeLabel(hhmm: string, format: "12h" | "24h"): string {
  const minutes = toMinutes(hhmm);
  if (minutes === null) return hhmm;
  if (format === "24h") return hhmm;
  if (minutes === 1440) return "12:00 AM (midnight)";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
}

/** What is wrong with one day's ranges, or null: a time that is not one, an end before its start,
 *  or two ranges overlapping. Ranges that only touch are fine. */
export function dayProblem(ranges: readonly AvailabilityRange[]): string | null {
  const spans: [number, number][] = [];
  for (const r of ranges) {
    const start = toMinutes(r.start);
    const end = toMinutes(r.end);
    if (start === null || end === null) return "Use times like 08:30";
    if (start >= end) return `${r.start}–${r.end} ends before it starts`;
    spans.push([start, end]);
  }
  spans.sort((a, b) => a[0] - b[0]);
  for (let i = 1; i < spans.length; i++) {
    if (spans[i]![0] < spans[i - 1]![1]) return `${fromMinutes(spans[i]![0])}–${fromMinutes(spans[i]![1])} overlaps another range`;
  }
  return null;
}

export function rangesOn(availability: readonly AvailabilityRange[], day: Weekday): AvailabilityRange[] {
  return availability.filter((r) => r.day === day);
}

/** The next range to add on a day: 09:00–17:00 on an empty day, else the hour after the last
 *  range ends, as far as the day allows. Null when the day is already full. */
export function nextRange(availability: readonly AvailabilityRange[], day: Weekday): AvailabilityRange | null {
  const ranges = rangesOn(availability, day);
  if (ranges.length === 0) return { day, start: "09:00", end: "17:00" };
  const lastEnd = Math.max(...ranges.map((r) => toMinutes(r.end) ?? 0));
  if (lastEnd >= 1440) return null;
  return { day, start: fromMinutes(lastEnd), end: fromMinutes(Math.min(lastEnd + 60, 1440)) };
}

/** Every day given the ranges `day` has, the days that had none included. */
export function copyToAllDays(availability: readonly AvailabilityRange[], day: Weekday): AvailabilityRange[] {
  const source = rangesOn(availability, day);
  return WEEKDAYS.flatMap((d) => source.map((r) => ({ ...r, day: d })));
}

/** The list with one item moved a place up (-1) or down (1); unchanged at either end. */
export function moveItem<T>(list: readonly T[], index: number, by: -1 | 1): T[] {
  const to = index + by;
  if (index < 0 || index >= list.length || to < 0 || to >= list.length) return [...list];
  const out = [...list];
  [out[index], out[to]] = [out[to]!, out[index]!];
  return out;
}

/** Every day, all day: a staff member's hours until someone sets custom ones. */
export function allDay(): AvailabilityRange[] {
  return WEEKDAYS.map((day) => ({ day, start: "00:00", end: "24:00" }));
}

/** Whether these hours are the 24-hour default: every day, one range from 00:00 to 24:00. */
export function isAllDay(availability: readonly AvailabilityRange[]): boolean {
  return (
    availability.length === WEEKDAYS.length &&
    WEEKDAYS.every((d) => {
      const r = rangesOn(availability, d);
      return r.length === 1 && r[0]!.start === "00:00" && r[0]!.end === "24:00";
    })
  );
}

/** Where custom hours start when switched on: weekdays, 9 to 5. */
export function officeHours(): AvailabilityRange[] {
  return (["mon", "tue", "wed", "thu", "fri"] as const).map((day) => ({ day, start: "09:00", end: "17:00" }));
}

/** A weekday as JavaScript's `Date#getDay` numbers it: Sunday is 0. */
export const JS_WEEKDAY: Record<Weekday, number> = {
  sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6,
};

export const WEEKDAY_SHORT: Record<Weekday, string> = {
  mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun",
};

/**
 * One line for a staff member's week, as the availability cards show it: "24 hours, every day",
 * "Weekdays, 9:00 AM to 5:00 PM", "Mon, Wed, 9:00 AM to 12:00 PM; Fri, 1:00 PM to 5:00 PM", or
 * "No hours". Days with the same ranges are grouped, in the calendar's week order.
 */
export function hoursSummary(
  availability: readonly AvailabilityRange[],
  format: "12h" | "24h",
  weekStartsOn: Weekday = "sun",
): string {
  if (availability.length === 0) return "No hours";
  if (isAllDay(availability)) return "24 hours, every day";
  const time = (hhmm: string) => (hhmm === "24:00" ? (format === "12h" ? "12:00 AM" : "24:00") : timeLabel(hhmm, format));
  const groups = new Map<string, Weekday[]>();
  for (const day of weekFrom(weekStartsOn)) {
    const ranges = sortRanges(rangesOn(availability, day));
    if (ranges.length === 0) continue;
    const key = ranges.map((r) => `${time(r.start)} to ${time(r.end)}`).join(", ");
    groups.set(key, [...(groups.get(key) ?? []), day]);
  }
  const name = (days: Weekday[]) => {
    const set = new Set(days);
    if (set.size === 7) return "Every day";
    if (set.size === 5 && ["mon", "tue", "wed", "thu", "fri"].every((d) => set.has(d as Weekday))) return "Weekdays";
    if (set.size === 2 && set.has("sat") && set.has("sun")) return "Weekends";
    return days.map((d) => WEEKDAY_SHORT[d]).join(", ");
  };
  return [...groups].map(([ranges, days]) => `${name(days)}, ${ranges}`).join("; ");
}

/** In week order, then by start, so a saved list reads the way the grid shows it. */
export function sortRanges(availability: readonly AvailabilityRange[]): AvailabilityRange[] {
  return [...availability].sort(
    (a, b) => WEEKDAYS.indexOf(a.day) - WEEKDAYS.indexOf(b.day) || a.start.localeCompare(b.start),
  );
}

// --- the edit page's form ------------------------------------------------------------------------

/** What the edit page holds: the calendar's settings and its staff, names kept for the screen. */
export type CalendarFormValues = Omit<Calendar, "id" | "tenant_id" | "created_at" | "updated_at">;

export function formFromCalendar(c: Calendar): CalendarFormValues {
  const { id: _id, tenant_id: _t, created_at: _c, updated_at: _u, ...rest } = c;
  return {
    ...rest,
    staff: c.staff.map((s) => ({ ...s, availability: sortRanges(s.availability) })),
  };
}

/** What `PATCH /calendars/{id}` is sent for these values: everything, staff replacing the list.
 *  The round-robin settings go only on a round-robin calendar, where the server fills a missing
 *  one; on any other type it refuses them. */
export function bodyFromForm(v: CalendarFormValues): CalendarUpdateInput {
  const roundRobin = v.calendar_type === "round_robin";
  const { staff, distribution, reschedule_owner, seats_per_session, ...rest } = v;
  return {
    ...rest,
    name: v.name.trim(),
    description: v.description?.trim() || null,
    ...(roundRobin
      ? { distribution: distribution ?? "availability", reschedule_owner: reschedule_owner ?? "keep" }
      : {}),
    ...(v.calendar_type === "class" ? { seats_per_session: seats_per_session ?? 10 } : {}),
    staff: staff.map((s) => ({
      user_id: s.user_id,
      priority: s.priority,
      location_kind: s.location_kind,
      location_detail: s.location_detail?.trim() || null,
      availability: sortRanges(s.availability),
    })),
  };
}

/**
 * What stops a save, keyed by where the page shows it: `name`, `staff`, `location.<userId>`,
 * `hours.<userId>.<day>`, and a rule's own field name. Empty when the form can be saved.
 */
export function formProblems(v: CalendarFormValues): Record<string, string> {
  const problems: Record<string, string> = {};
  if (!v.name.trim()) problems.name = "A calendar needs a name";
  else if (v.name.trim().length > 120) problems.name = "Keep the name to 120 characters";
  if (singleHost(v.calendar_type) && v.staff.length > 1) {
    problems.staff = `A ${TYPE_INFO[v.calendar_type].label.toLowerCase()} calendar has one host: keep one staff member`;
  }
  for (const s of v.staff) {
    if ((s.location_kind === "address" || s.location_kind === "custom") && !s.location_detail?.trim()) {
      problems[`location.${s.user_id}`] =
        s.location_kind === "address" ? "Add the address" : "Add the meeting details";
    }
    for (const day of WEEKDAYS) {
      const problem = dayProblem(rangesOn(s.availability, day));
      if (problem) problems[`hours.${s.user_id}.${day}`] = problem;
    }
  }
  const whole = (key: keyof CalendarFormValues, min: number, max: number, what: string) => {
    const n = v[key] as number;
    if (!Number.isInteger(n) || n < min || n > max) problems[key] = `${what}: ${min} to ${max}`;
  };
  whole("duration_minutes", 5, 1440, "Minutes");
  if (v.calendar_type === "class") {
    const n = v.seats_per_session ?? 10;
    if (!Number.isInteger(n) || n < 1 || n > 500) problems.seats_per_session = "Seats: 1 to 500";
  }
  whole("slot_interval_minutes", 5, 1440, "Minutes");
  whole("min_notice_minutes", 0, 60 * 24 * 90, "Minutes");
  whole("pre_buffer_minutes", 0, 1440, "Minutes");
  whole("post_buffer_minutes", 0, 1440, "Minutes");
  whole("booking_window_days", 1, 365, "Days");
  return problems;
}

/** Which section of the edit page each problem is shown in, so the section nav can mark it. */
export type CalendarSection = "details" | "staff" | "availability" | "rules" | "preview";

export function sectionOf(problemKey: string): CalendarSection {
  if (problemKey === "name") return "details";
  if (problemKey === "staff" || problemKey.startsWith("location.")) return "staff";
  if (problemKey.startsWith("hours.")) return "availability";
  return "rules";
}

/** A staff member added on the page: medium priority, meeting by phone, available 24 hours every
 *  day until custom hours are set. */
export function newStaff(user: { id: number; display_name: string | null; email: string }): CalendarStaff {
  return {
    user_id: user.id,
    display_name: user.display_name,
    email: user.email,
    user_active: true,
    priority: "medium",
    location_kind: "phone",
    location_detail: null,
    availability: allDay(),
  };
}

export function staffName(s: { display_name: string | null; email: string }): string {
  return s.display_name?.trim() || s.email;
}

// --- slots -------------------------------------------------------------------------------

/** The day an instant falls on, on `timezone`'s clock: "2026-10-05". */
export function localDay(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit" })
    .format(new Date(iso));
}

/** "2026-10-05" plus `n` days, as a calendar date: no clock, so no DST. */
export function addDays(day: string, n: number): string {
  const [y, m, d] = day.split("-").map(Number);
  const next = new Date(Date.UTC(y!, m! - 1, d! + n));
  return next.toISOString().slice(0, 10);
}

/** "Mon, Oct 5" for a calendar date. */
export function dayLabel(day: string, locale = "en-US"): string {
  const [y, m, d] = day.split("-").map(Number);
  return new Intl.DateTimeFormat(locale, { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" })
    .format(new Date(Date.UTC(y!, m! - 1, d!)));
}

/** An instant's time of day on the calendar's clock: "9:00 AM", or "09:00" on a 24-hour one. */
export function slotTime(iso: string, timezone: string, format: "12h" | "24h", locale = "en-US"): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    hour: format === "12h" ? "numeric" : "2-digit",
    minute: "2-digit",
    hour12: format === "12h",
  }).format(new Date(iso));
}

/** Every day from `first` to `last` with the slots that start on it, on the calendar's clock;
 *  a day with none is kept, so a week reads as a week. */
export function slotsByDay(
  slots: readonly CalendarSlot[],
  timezone: string,
  first: string,
  last: string,
): { day: string; slots: CalendarSlot[] }[] {
  const days: { day: string; slots: CalendarSlot[] }[] = [];
  for (let day = first; day <= last; day = addDays(day, 1)) days.push({ day, slots: [] });
  const index = new Map(days.map((d) => [d.day, d]));
  for (const s of slots) index.get(localDay(s.start, timezone))?.slots.push(s);
  return days;
}
